import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { E } from "@/components/Editable";
import { MoonStars, SignOut, PencilSimple, CalendarCheck } from "@phosphor-icons/react";

const links = [
  { to: "/reading", label: "Reading Room" },
  { to: "/book", label: "Book a Call" },
  { to: "/dashboard", label: "My Journey" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { isAdminEditing, enterEditMode } = useContent();
  const navigate = useNavigate();
  const location = useLocation();

  if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/edit")) return null;

  return (
    <header className="fixed top-0 left-0 right-0 z-40 glass-panel border-b border-white/10" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo-link">
          <MoonStars size={26} weight="duotone" className="text-gold" />
          <span className="font-heading text-2xl font-semibold tracking-tighter text-foreground">
            <E k="nav_brand" f="ARCANA" />
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.slice(1)}`}
              className={`text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:text-gold ${
                location.pathname === l.to ? "text-gold" : "text-muted-foreground"
              }`}
            >
              <E k={`nav_label_${l.to.slice(1)}`} f={l.label} />
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-3 sm:gap-4">
          {location.pathname !== "/book" && (
            <Link
              to="/book"
              data-testid="nav-book-now-button"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gold text-black text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#e8c95a] transition-colors duration-300"
            >
              <CalendarCheck size={14} weight="bold" /> <E k="nav_book_now" f="Book Now" />
            </Link>
          )}
          {user?.role === "admin" && !isAdminEditing && (
            <button
              onClick={enterEditMode}
              data-testid="nav-edit-site-button"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-gold/50 text-gold text-[10px] uppercase tracking-[0.2em] hover:bg-gold hover:text-black transition-colors duration-300"
            >
              <PencilSimple size={14} weight="duotone" /> Edit Website
            </button>
          )}
          {user ? (
            <>
              <span className="hidden sm:block text-xs text-muted-foreground" data-testid="nav-user-name">
                {user.name || user.email}
              </span>
              <button
                onClick={async () => { await logout(); navigate("/"); }}
                data-testid="nav-logout-button"
                className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground hover:text-gold transition-colors duration-300"
              >
                <SignOut size={16} /> Exit
              </button>
            </>
          ) : (
            <Link
              to="/auth"
              data-testid="nav-login-link"
              className="px-5 py-2 rounded-full border border-gold/50 text-gold text-xs uppercase tracking-[0.2em] hover:bg-gold hover:text-black transition-colors duration-300"
            >
              Enter
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};
